import { Component, type ErrorInfo, type ReactNode } from 'react'
import { AlertTriangle, RotateCcw } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { postCrash, currentRoute } from '@/services/telemetry'

interface ErrorBoundaryProps {
  children: ReactNode
  /** Optional custom UI; defaults to a generic apology card. */
  fallback?: (error: Error, reset: () => void) => ReactNode
}

interface ErrorBoundaryState {
  error: Error | null
}

/**
 * Top-level safety net for uncaught render errors. Without this, a single
 * runtime error anywhere in the tree blanks the whole app to a white screen
 * (React's default for unhandled errors in production builds).
 *
 * This is intentionally **lifecycle**-based (not hook-based) — React error
 * boundaries can only be implemented as class components. The telemetry
 * pipeline catches the rendered error in componentDidCatch and forwards it
 * to /telemetry/crashes so the admin App Health page's Crashes tab shows
 * the captured exception + component stack alongside mobile crashes.
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { error: null }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error }
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // Keep the console line for live debugging; the network mirror runs
    // in parallel so a backend outage doesn't lose engineer visibility.
    console.error('[ErrorBoundary] Caught:', error, info.componentStack)
    void postCrash({
      exception: error.message ?? String(error),
      stack_trace:
        (error.stack ?? 'no stack')
        + (info.componentStack ? '\n\n--- React component stack ---\n' + info.componentStack : ''),
      route: currentRoute(),
    })
  }

  reset = (): void => {
    this.setState({ error: null })
  }

  render(): ReactNode {
    const { error } = this.state
    if (!error) return this.props.children
    if (this.props.fallback) return this.props.fallback(error, this.reset)

    return (
      <div className="min-h-screen flex items-center justify-center p-6 bg-background">
        <div className="max-w-md w-full rounded-lg border bg-card p-6 text-center space-y-4">
          <div className="mx-auto h-12 w-12 rounded-full bg-destructive/10 flex items-center justify-center">
            <AlertTriangle className="h-6 w-6 text-destructive" />
          </div>
          <div>
            <h1 className="text-lg font-semibold">Something went wrong</h1>
            <p className="text-sm text-muted-foreground mt-1">
              The page hit an unexpected error and couldn&rsquo;t finish loading.
              You can try again or reload the page.
            </p>
          </div>
          {import.meta.env.DEV && (
            <pre className="text-left text-xs bg-muted p-3 rounded overflow-x-auto whitespace-pre-wrap">
              {error.message}
            </pre>
          )}
          <div className="flex gap-2 justify-center">
            <Button variant="outline" onClick={() => window.location.reload()}>
              Reload page
            </Button>
            <Button onClick={this.reset}>
              <RotateCcw className="h-4 w-4 mr-2" />
              Try again
            </Button>
          </div>
        </div>
      </div>
    )
  }
}
